import {vertex, fragment} from './photon-shaders';

/** Three passes: soft light, fine particles, then shaded micro-volumes. */
export function createPhotonRenderer(canvas: HTMLCanvasElement, count: number) {
  const gl = canvas.getContext('webgl', {alpha: false, antialias: false});
  if (!gl || gl.isContextLost()) return null;

  const shaders: WebGLShader[] = [];
  const buffers: WebGLBuffer[] = [];
  const program = gl.createProgram();
  const dispose = () => {
    buffers.forEach(buffer => gl.deleteBuffer(buffer));
    shaders.forEach(shader => gl.deleteShader(shader));
    if (program) gl.deleteProgram(program);
  };

  try {
    if (!program) throw new Error('Program unavailable');
    for (const [type, source] of [[gl.VERTEX_SHADER, vertex], [gl.FRAGMENT_SHADER, fragment]] as const) {
      const shader = gl.createShader(type);
      if (!shader) throw new Error('Shader unavailable');
      shaders.push(shader);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error('Shader compilation failed');
      gl.attachShader(program, shader);
    }
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error('Program linking failed');
    gl.useProgram(program);

    // Keep the selected B composition deterministic across visits and resizes.
    const points = new Float32Array(count * 4);
    let rng = 4197;
    for (let i = 0; i < points.length; i++) {
      rng = (Math.imul(rng, 1664525) + 1013904223) | 0;
      points[i] = (rng >>> 0) / 4294967296;
    }
    const beads: number[][] = [];
    for (let i = 0; i < count; i++) {
      const offset = i * 4;
      if (points[offset + 3] >= .992) beads.push(Array.from(points.subarray(offset, offset + 4)));
    }
    // Opaque-looking spheres need back-to-front alpha compositing.
    beads.sort((a, b) => b[2] - a[2]);
    const createBuffer = (data: Float32Array) => {
      const buffer = gl.createBuffer();
      if (!buffer) throw new Error('Buffer unavailable');
      buffers.push(buffer);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
      return buffer;
    };
    const particleBuffer = createBuffer(points);
    const beadBuffer = createBuffer(new Float32Array(beads.flat()));
    const seed = gl.getAttribLocation(program, 'seed');
    gl.enableVertexAttribArray(seed);
    const uTime = gl.getUniformLocation(program, 'time');
    const uPointer = gl.getUniformLocation(program, 'pointer');
    const uPass = gl.getUniformLocation(program, 'pass');
    const uScale = gl.getUniformLocation(program, 'pointScale');
    let pointScale = 1;
    const bind = (buffer: WebGLBuffer) => {
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.vertexAttribPointer(seed, 4, gl.FLOAT, false, 0, 0);
    };

    return {
      resize(scale: number) { pointScale = scale; },
      draw(time: number, x: number, y: number) {
        if (gl.isContextLost()) return;
        gl.viewport(0, 0, canvas.width, canvas.height);
        gl.clearColor(16 / 255, 14 / 255, 25 / 255, 1);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.enable(gl.BLEND);
        gl.uniform1f(uTime, time);
        gl.uniform2f(uPointer, x, y);
        gl.uniform1f(uScale, pointScale);
        bind(particleBuffer);
        gl.blendFunc(gl.ONE, gl.ONE);
        gl.uniform1f(uPass, 1);
        gl.drawArrays(gl.POINTS, 0, count);
        gl.uniform1f(uPass, 0);
        gl.drawArrays(gl.POINTS, 0, count);
        bind(beadBuffer);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
        gl.uniform1f(uPass, 2);
        gl.drawArrays(gl.POINTS, 0, beads.length);
      },
      dispose,
    };
  } catch {
    dispose();
    return null;
  }
}
